/*
 * Shared Sentry logging helpers.
 *
 * Loaded before attribution.js (and other theme scripts) so that
 * window.logToSentry / window.logExceptionToSentry are available everywhere,
 * including attribution.js's synchronous outer try/catch.
 *
 * Both helpers degrade gracefully to the console if the Sentry SDK has not
 * loaded yet.
 *
 * CAB-3618 adds an attribution GUID on top of that:
 *
 *   - One GUID per browser, mirrored onto the cart so it lands in the order's
 *     note_attributes and an order can be joined back to the frontend events
 *     that produced it.
 *   - The GUID is stamped into the *message text* of every outgoing event, not
 *     just onto a tag. Sentry's free-text search matches the message, so a tag
 *     alone is invisible to a bare GUID paste into the search box.
 *   - Every console.warn / console.error in the theme is forwarded, so failures
 *     that were only ever console-logged still produce a searchable event.
 *
 * All of this is added here rather than in a second SDK bundle: theme.liquid
 * already loads the Sentry CDN loader script, and this file already owns
 * window.logToSentry, so a second SDK would double-load Sentry and whichever
 * file ran last would clobber the other's helpers.
 */
(() => {
  const STAGING_HOST = 'cabi-clothing-staging.myshopify.com';

  // localStorage key, and the cart/order attribute the GUID is written to.
  // Keep GUID_ATTR in sync with the attributes[AttributionGUID] param in
  // snippets/global-variables.liquid (window.customCheckout).
  const GUID_KEY = 'ATTRIBUTION_GUID';
  const GUID_ATTR = 'AttributionGUID';
  const GUID_CART_SYNCED_KEY = 'CART_ATTRIBUTION_GUID_SYNCED';

  // Storage throws in Safari private mode / when cookies are blocked.
  const readStore = (store, key) => {
    try {
      return window[store].getItem(key);
    } catch (e) {
      return null;
    }
  };

  const writeStore = (store, key, value) => {
    try {
      window[store].setItem(key, value);
    } catch (e) {
      /* non-fatal */
    }
  };

  // Which theme served this pageview.
  //
  // Read from window.Shopify.theme, NOT Liquid's `theme` object - Shopify
  // documents that one as deprecated: "the values of this object's properties
  // are subject to change, so can't be relied on within the theme".
  //
  // theme_role is the field that matters. "main" is the published theme real
  // shoppers see; "development" is a `shopify theme dev` session on someone's
  // laptop. Without it, an event from a stale local branch is indistinguishable
  // from a production bug - which is exactly how a CAB-2839-era message with no
  // GUID turned up looking like a gap in this instrumentation.
  const themeInfo = () => {
    try {
      const t = (window.Shopify && window.Shopify.theme) || null;
      if (!t) return {};
      const out = {};
      if (t.id != null) out.theme_id = String(t.id);
      if (t.name) out.theme_name = t.name;
      if (t.role) out.theme_role = t.role;
      return out;
    } catch (e) {
      return {};
    }
  };

  const newGuid = () => {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (e) {
      /* fall through */
    }
    // crypto.randomUUID needs a secure context; fall back so preview/proxy
    // hosts served over http still get a usable id.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  };

  let guid = readStore('localStorage', GUID_KEY);
  let guidIsFresh = false;
  if (!guid) {
    guid = newGuid();
    guidIsFresh = true;
    writeStore('localStorage', GUID_KEY, guid);
  }
  window.ATTRIBUTION_GUID = guid;

  const guidStamp = () => `[guid:${guid}]`;

  /*
   * Stamp identity onto an outgoing event. Idempotent, so it is safe whether it
   * runs via the global event processor or the per-call fallback below.
   *
   * Grouping is pinned BEFORE the message is touched: appending a per-visitor id
   * to the message would otherwise defeat Sentry's message-based grouping and
   * turn every single event into its own issue.
   */
  const stampEvent = (event) => {
    try {
      if (!event.fingerprint) {
        event.fingerprint = event.exception
          ? ['{{ default }}'] // exceptions: keep grouping by stack trace
          : [event.message || '{{ default }}']; // messages: group on pre-GUID text
      }

      const stamp = guidStamp();
      if (!event.message) {
        // Exception events carry no message; giving them one puts the GUID in
        // the searchable text without changing the issue title. Repeat the
        // exception's own type and value into it so the message column stays
        // readable instead of showing a bare stamp. Grouping is unaffected —
        // the fingerprint above already pinned these to default (stack trace).
        const values = event.exception && event.exception.values;
        const first = values && values.length ? values[values.length - 1] : null;
        const label = first
          ? `${first.type || 'Error'}${first.value ? ': ' + first.value : ''}`
          : '';
        event.message = label ? `${label} ${stamp}` : stamp;
      } else if (event.message.indexOf(stamp) === -1) {
        event.message = `${event.message} ${stamp}`;
      }

      event.tags = event.tags || {};
      event.tags.attribution_guid = guid;

      const t = themeInfo();
      if (t.theme_id && !event.tags.theme_id) event.tags.theme_id = t.theme_id;
      if (t.theme_role && !event.tags.theme_role) event.tags.theme_role = t.theme_role;

      // The loader script auto-inits without an environment, so set it here
      // rather than reconfiguring init.
      if (!event.environment) {
        event.environment =
          window.location.hostname === STAGING_HOST ? 'staging' : 'production';
      }

      event.user = event.user || {};
      if (!event.user.id) event.user.id = guid;
    } catch (e) {
      /* never let logging break the page */
    }
    return event;
  };

  // Prefer a global event processor: it catches unhandled errors and rejections
  // too, not only the events these helpers send. The loader script fetches the
  // SDK lazily, so fall back to Sentry.onLoad, then to per-call stamping.
  let processorInstalled = false;
  const installProcessor = () => {
    try {
      if (typeof Sentry !== 'undefined' && typeof Sentry.addEventProcessor === 'function') {
        Sentry.addEventProcessor(stampEvent);
        processorInstalled = true;
      }
    } catch (e) {
      /* fall back to per-call stamping */
    }
    return processorInstalled;
  };

  if (!installProcessor()) {
    try {
      if (typeof Sentry !== 'undefined' && typeof Sentry.onLoad === 'function') {
        Sentry.onLoad(installProcessor);
      }
    } catch (e) {
      /* fall back to per-call stamping */
    }
  }

  // ALWAYS stamp here. This used to be skipped when the event processor had
  // registered, on the assumption the processor would do it - but registering it
  // on the loader's SDK does not make it apply, so those events went out with no
  // stamp at all. Worse, it was a race: the loader fetches the SDK lazily, so
  // whether `processorInstalled` had flipped by the time attribution ran varied
  // per page load, and the same message was stamped on one visit and not the
  // next. stampEvent is idempotent, so stamping here is safe either way.
  const stampMessage = (message) => {
    const stamp = guidStamp();
    return message.indexOf(stamp) === -1 ? `${message} ${stamp}` : message;
  };

  // Safely send message events to Sentry.
  const logToSentry = (message, level = 'info', extra = {}) => {
    try {
      if (typeof Sentry !== 'undefined') {
        Sentry.captureMessage(stampMessage(message), {
          level: level,
          // Pin grouping to the pre-GUID text for the fallback path too.
          fingerprint: [message],
          tags: Object.assign({ attribution_guid: guid }, themeInfo()),
          extra: {
            ...extra,
            attribution_guid: guid,
            ...themeInfo(),
            timestamp: new Date().toISOString(),
            location: window.location.href,
            referrer: document.referrer
          }
        });
      } else {
        console.log(`[Sentry Deferred] [${level}] ${message}`, extra);
      }
    } catch (err) {
      console.error('Failed to log to Sentry:', err);
    }
  };

  // Safely send exceptions to Sentry.
  const logExceptionToSentry = (exception, extra = {}) => {
    try {
      if (typeof Sentry !== 'undefined') {
        Sentry.captureException(exception, {
          tags: Object.assign({ attribution_guid: guid }, themeInfo()),
          extra: {
            ...extra,
            attribution_guid: guid,
            ...themeInfo(),
            timestamp: new Date().toISOString(),
            location: window.location.href,
            referrer: document.referrer
          }
        });
      } else {
        console.error('[Sentry Deferred] Exception:', exception, extra);
      }
    } catch (err) {
      console.error('Failed to log exception to Sentry:', err);
    }
  };

  window.logToSentry = logToSentry;
  window.logExceptionToSentry = logExceptionToSentry;

  /*
   * Forward console.warn / console.error to Sentry.
   *
   * Anything reported only to the console was previously invisible. console.log
   * is deliberately left alone — there are ~58 sites, many in theme.js hot
   * paths, and they would swamp the project.
   */
  const CONSOLE_LEVELS = { warn: 'warning', error: 'error' };
  let capturingConsole = false;

  Object.keys(CONSOLE_LEVELS).forEach((method) => {
    const original = console[method];
    if (typeof original !== 'function') return;

    console[method] = function () {
      const args = Array.prototype.slice.call(arguments);
      // Re-entry guard: this file and the SDK both log through console on
      // trouble, which would otherwise recurse.
      if (!capturingConsole) {
        capturingConsole = true;
        try {
          const text = args
            .map((a) => {
              if (typeof a === 'string') return a;
              if (a instanceof Error) return a.message;
              try {
                return JSON.stringify(a);
              } catch (e) {
                return String(a);
              }
            })
            .join(' ')
            .slice(0, 500);

          // Skip our own and the SDK's chatter so it cannot feed itself.
          if (
            text &&
            text.indexOf('[Sentry') !== 0 &&
            text.indexOf('Sentry Logger') === -1 &&
            text.indexOf('Failed to log to Sentry') === -1 &&
            text.indexOf('Failed to log exception to Sentry') === -1
          ) {
            logToSentry(text, CONSOLE_LEVELS[method], { source: `console.${method}` });
          }
        } catch (e) {
          /* logging must never break the page */
        } finally {
          capturingConsole = false;
        }
      }
      return original.apply(console, args);
    };
  });

  /*
   * Log every theme:partner:* attribution event.
   *
   * attribution.js logs its own resolution, but it is not the only thing that
   * changes attribution. The stylist selector, the find-your-consultant locator,
   * the enrollment flow and the header's "remove consultant" link between them
   * dispatch these events from 8 places across 4 files, none of which call
   * Sentry at all — so every shopper-initiated stylist change was invisible.
   * Listening on `document` here covers all of them, including future
   * dispatchers, without editing any of those files.
   *
   * Message text is deliberately kept constant per event type (the stylist goes
   * in the tags and payload, not the string) so each type stays a single Sentry
   * issue rather than one issue per stylist.
   */
  const ATTRIBUTION_EVENTS = [
    'theme:partner:render',
    'theme:partner:mismatch',
    'theme:partner:unavailable',
    'theme:partner:update',
    'theme:partner:find',
    'theme:partner:continue-without',
  ];

  const lastSeen = Object.create(null);

  ATTRIBUTION_EVENTS.forEach((type) => {
    document.addEventListener(type, (event) => {
      try {
        const detail = (event && event.detail) || {};
        const partner = detail.partner || detail.current || null;
        const siteName = (partner && partner.siteName) || null;
        const stylistId = (partner && partner.stylistId) || null;
        const preferredSite = (detail.preferred && detail.preferred.siteName) || null;

        // Collapse repeats only when there is partner data to compare.
        // theme:partner:find and :continue-without carry no detail at all, so
        // deduping those would record only the first of a shopper's repeated
        // interactions with the selector.
        if (siteName || stylistId || preferredSite) {
          const signature = `${siteName}|${stylistId}|${preferredSite}`;
          if (lastSeen[type] === signature) return;
          lastSeen[type] = signature;
        }

        logToSentry(`Attribution event: ${type}`, 'info', {
          attribution_event: type,
          site_name: siteName,
          stylist_id: stylistId,
          preferred_site_name: preferredSite,
          partner_status: (partner && partner.status) || null,
          // partner:render with a null partner means attribution was CLEARED —
          // the remove-consultant link and the enrollment reset both do this.
          cleared: Object.prototype.hasOwnProperty.call(detail, 'partner') && !detail.partner,
          current_site: readStore('localStorage', 'CURRENT_SITE'),
          current_show_id: readStore('localStorage', 'CURRENT_SHOW_ID'),
          page: window.location.pathname,
        });
      } catch (e) {
        /* logging must never break a UI event handler */
      }
    });
  });

  /*
   * Mirror the GUID onto the cart so it surfaces in the order's note_attributes.
   *
   * The cart is read BEFORE writing, because the cart outlives localStorage and
   * must win. Safari's ITP deletes script-writable storage after 7 idle days
   * while Shopify's server-set cart cookie survives, and in private mode
   * localStorage throws outright — in both cases a returning shopper would
   * otherwise be handed a second GUID that clobbers the one already on their
   * cart, orphaning every event logged before that point from the eventual
   * order.
   *
   * window.customCheckout appends the GUID to the checkout URL, covering the
   * main checkout path; the write here covers the fallback paths that navigate
   * straight to /checkout and makes the GUID visible on abandoned carts.
   * Runs once per tab session: one GET, plus one POST only when needed.
   */
  const reconcileGuidWithCart = async () => {
    if (readStore('sessionStorage', GUID_CART_SYNCED_KEY) === guid) return;

    const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';

    try {
      const cartRes = await fetch(`${cartUrl}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = cartRes.ok ? await cartRes.json() : null;
      const cartGuid = cart && cart.attributes && cart.attributes[GUID_ATTR];

      if (cartGuid && cartGuid !== 'NONE') {
        if (cartGuid !== guid) {
          logToSentry('Attribution: adopted existing AttributionGUID from cart', 'info', {
            cart_guid: cartGuid,
            local_guid: guid,
            // True when localStorage was empty/unavailable — the ITP and
            // private-mode cases. False means the two genuinely diverged.
            local_guid_was_freshly_minted: guidIsFresh,
          });
          guid = cartGuid;
          window.ATTRIBUTION_GUID = cartGuid;
          writeStore('localStorage', GUID_KEY, cartGuid);
        }
        writeStore('sessionStorage', GUID_CART_SYNCED_KEY, guid);
        return;
      }

      const res = await fetch(`${cartUrl}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [GUID_ATTR]: guid } }),
      });

      if (res.ok) {
        writeStore('sessionStorage', GUID_CART_SYNCED_KEY, guid);
      } else {
        logToSentry(`Failed to set ${GUID_ATTR} cart attribute`, 'warning', {
          status: res.status,
        });
      }
    } catch (err) {
      logExceptionToSentry(err, { context: 'reconcileGuidWithCart' });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileGuidWithCart);
  } else {
    reconcileGuidWithCart();
  }
})();
